import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../Icon";

const SIZES = {
  sm: 360,
  md: 480,
  lg: 720,
  xl: 960,
};

const OPEN_MS = 200;
const CLOSE_MS = 160;

/**
 * Modal DS nativo — portal manual con backdrop, cierre con ESC y click fuera,
 * focus al primer elemento focusable. Sin reactstrap.
 */
export function Modal({
  open,
  onClose,
  title,
  size = "md",
  children,
  closeOnBackdrop = true,
  className,
}) {
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);
  const [mounted, setMounted] = useState(open);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let r2 = 0;
      const r1 = requestAnimationFrame(() => {
        r2 = requestAnimationFrame(() => setActive(true));
      });
      return () => {
        cancelAnimationFrame(r1);
        if (r2) cancelAnimationFrame(r2);
      };
    }
    setActive(false);
    const t = setTimeout(() => setMounted(false), CLOSE_MS);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!mounted) return undefined;
    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      if (!dialogRef.current) return;
      const focusable = dialogRef.current.querySelector(
        'input,select,textarea,button,[tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }, 30);

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [mounted, onClose]);

  if (!mounted) return null;

  const width = SIZES[size] ?? SIZES.md;

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) {
          onClose?.();
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,19,22,0.45)",
        zIndex: 1090,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "60px 20px 20px",
        overflowY: "auto",
        opacity: active ? 1 : 0,
        transition: `opacity ${active ? OPEN_MS : CLOSE_MS}ms var(--kr-ease-out)`,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={className}
        style={{
          background: "var(--kr-bg-surface)",
          borderRadius: "var(--kr-radius-lg)",
          boxShadow: "var(--kr-shadow-lg)",
          width: "100%",
          maxWidth: width,
          maxHeight: "calc(100vh - 80px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          opacity: active ? 1 : 0,
          transform: active ? "translateY(0) scale(1)" : "translateY(-8px) scale(0.98)",
          transition: active
            ? `opacity ${OPEN_MS}ms var(--kr-ease-out) 30ms, transform ${OPEN_MS}ms var(--kr-ease-out) 30ms`
            : `opacity ${CLOSE_MS}ms var(--kr-ease-in), transform ${CLOSE_MS}ms var(--kr-ease-in)`,
        }}
      >
        {title != null && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "14px 20px",
              borderBottom: "1px solid var(--kr-border-subtle)",
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--kr-fg-strong)",
              }}
            >
              {title}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--kr-fg-muted)",
                padding: 4,
                borderRadius: 6,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--kr-bg-subtle)";
                e.currentTarget.style.color = "var(--kr-fg-strong)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--kr-fg-muted)";
              }}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ModalBody({ children, style }) {
  return (
    <div
      style={{
        padding: 20,
        background: "var(--kr-bg-surface)",
        color: "var(--kr-fg-primary)",
        fontSize: 13,
        lineHeight: 1.5,
        ...(style || {}),
      }}
    >
      {children}
    </div>
  );
}

export function ModalFooter({ children, style }) {
  return (
    <div
      style={{
        padding: "12px 20px",
        background: "var(--kr-bg-surface)",
        borderTop: "1px solid var(--kr-border-subtle)",
        display: "flex",
        gap: 8,
        justifyContent: "flex-end",
        ...(style || {}),
      }}
    >
      {children}
    </div>
  );
}

export default Modal;
